import React, { FC, Suspense, useCallback, useState } from 'react';
import ContentLoader from 'react-content-loader';
import { graphql, useLazyLoadQuery, useMutation } from 'react-relay';
import { T } from 'src/shared/helpers';
import theme from 'src/shared/theme';
import { TOOL_DEFINITIONS } from '../common/constants';
import {
    FormGroup,
    FormInput,
    FormLabel,
    FormSelect,
    SectionCard,
    SectionCardTitle,
    SettingHint,
    SharedWarningCallout,
    StackedBody,
    StackedBreadcrumb
} from '../common/styled';
import type { moqRuleEditor_MoqRuleDetailQuery } from './__generated__/moqRuleEditor_MoqRuleDetailQuery.graphql';
import type { moqRuleEditor_UpdateMoqRuleMutation } from './__generated__/moqRuleEditor_UpdateMoqRuleMutation.graphql';

export interface MoqRuleEditorProps {
    itemId: string;
    productId: string;
    toolKey: string;
    onBack: () => void;
    onSave: () => void;
    saveRef: React.MutableRefObject<(() => Promise<void>) | null>;
}

const moqRuleDetailQuery = graphql`
    query moqRuleEditor_MoqRuleDetailQuery($id: ID!) {
        moqRule(id: $id) {
            id
            moqRuleID
            name
            minimumQuantity
            stepType
            fixedStep
            customTiers
            products {
                productID
            }
            printTypes {
                printTypeID
            }
            colors {
                colorID
                productID
            }
            globalColors
        }
    }
`;

const MoqRuleEditorContent: FC<MoqRuleEditorProps> = ({ itemId, productId, toolKey, onBack, onSave, saveRef }) => {
    const toolName = TOOL_DEFINITIONS.find((tool) => tool.key === toolKey)?.name || '';
    const productNameData = useLazyLoadQuery<any>(
        graphql`
            query moqRuleEditor_ProductNameQuery($id: ID!) {
                node(id: $id) {
                    ... on Model {
                        name
                    }
                }
            }
        `,
        { id: productId }
    );
    const productName = (productNameData?.node as any)?.name || '';

    const json = useLazyLoadQuery<moqRuleEditor_MoqRuleDetailQuery>(moqRuleDetailQuery, { id: itemId });
    const rule = json?.moqRule;
    const ruleName = rule?.name || '';
    const assignedProductCount = rule?.products?.length;

    const [minimumQuantity, setMinimumQuantity] = useState(() => String(rule?.minimumQuantity ?? 1));
    const [stepType, setStepType] = useState(() => rule?.stepType || 'FIXED');
    const [fixedStep, setFixedStep] = useState(() => String(rule?.fixedStep ?? 1));
    const [customTiers, setCustomTiers] = useState(() => rule?.customTiers || '');

    const [commitUpdateMoq] = useMutation<moqRuleEditor_UpdateMoqRuleMutation>(graphql`
        mutation moqRuleEditor_UpdateMoqRuleMutation($input: MoqRuleInput!) {
            updateMoqRule(input: $input) {
                moqRule {
                    id
                    name
                    minimumQuantity
                    stepType
                    fixedStep
                    customTiers
                }
            }
        }
    `);

    const handleSave = useCallback(async () => {
        if (!rule) return;
        try {
            await new Promise<void>((resolve, reject) => {
                commitUpdateMoq({
                    variables: {
                        input: {
                            id: rule.id,
                            name: rule.name,
                            minimumQuantity: Number(minimumQuantity),
                            stepType: stepType,
                            fixedStep: Number(fixedStep),
                            customTiers: customTiers,
                            products: rule.products.map((p) => p.productID),
                            printTypes: rule.printTypes.map((p) => p.printTypeID),
                            colors: rule.colors.map((c) => ({ colorID: c.colorID, productID: c.productID })),
                            globalColors: rule.globalColors as string[]
                        }
                    },
                    onCompleted: () => {
                        onSave();
                        resolve();
                    },
                    onError: (error) => reject(error)
                });
            });
        } catch {
            /* ignore */
        }
    }, [rule, minimumQuantity, stepType, fixedStep, customTiers, onSave, commitUpdateMoq]);

    saveRef.current = handleSave;

    return (
        <>
            <StackedBreadcrumb>
                <span onClick={onBack}>{productName}</span> / {toolName} / {ruleName}
            </StackedBreadcrumb>
            <StackedBody>
                {assignedProductCount != null && assignedProductCount > 1 && (
                    <SharedWarningCallout>
                        {T._('This rule is assigned to', 'Admin')} <strong>{assignedProductCount}</strong>{' '}
                        {T._('products. Any changes will apply to all of them.', 'Admin')}
                    </SharedWarningCallout>
                )}
                <SectionCard>
                    <SectionCardTitle>{T._('Quantity Threshold', 'Admin')}</SectionCardTitle>

                    <FormGroup>
                        <FormLabel>{T._('Minimum quantity per order', 'Admin')}</FormLabel>
                        <FormInput type='number' min={1} value={minimumQuantity} onChange={(e) => setMinimumQuantity(e.target.value)} />
                    </FormGroup>
                </SectionCard>

                <SectionCard>
                    <SectionCardTitle>{T._('Quantity increments', 'Admin')}</SectionCardTitle>

                    <FormGroup>
                        <FormLabel>{T._('Increment type', 'Admin')}</FormLabel>
                        <FormSelect value={stepType} onChange={(e) => setStepType(e.target.value as 'FIXED' | 'CUSTOM')}>
                            <option value='FIXED'>{T._('Fixed steps', 'Admin')}</option>
                            <option value='CUSTOM'>{T._('Custom tiers', 'Admin')}</option>
                        </FormSelect>
                    </FormGroup>

                    {stepType === 'FIXED' && (
                        <FormGroup>
                            <FormLabel>{T._('Step value', 'Admin')}</FormLabel>
                            <SettingHint>
                                {T._('Customers can only increase the quantity by the step value you set (e.g. step of 5: 10, 15, 20, 25...)', 'Admin')}
                            </SettingHint>
                            <FormInput type='number' min={1} value={fixedStep} onChange={(e) => setFixedStep(e.target.value)} />
                        </FormGroup>
                    )}

                    {stepType === 'CUSTOM' && (
                        <FormGroup>
                            <FormLabel>{T._('Tier values', 'Admin')}</FormLabel>
                            <SettingHint>{T._('Define specific quantity levels that customers can choose from (e.g. 10, 25, 50, 100).', 'Admin')}</SettingHint>
                            <FormInput type='text' value={customTiers} onChange={(e) => setCustomTiers(e.target.value)} placeholder='10,25,50,100' />
                        </FormGroup>
                    )}
                </SectionCard>
            </StackedBody>
        </>
    );
};

export const MoqRuleEditor: FC<MoqRuleEditorProps> = (props) => {
    return (
        <Suspense
            fallback={
                <>
                    <StackedBreadcrumb>
                        <span onClick={props.onBack}>{T._('Loading...', 'Admin')}</span>
                    </StackedBreadcrumb>
                    <StackedBody>
                        <ContentLoader
                            speed={2}
                            width='100%'
                            height='60'
                            backgroundColor={theme.skeleton.backgroundColor}
                            foregroundColor={theme.skeleton.foregroundColor}
                        >
                            <rect x='0' y='0' rx='8' ry='8' width='100%' height='16' />
                            <rect x='0' y='24' rx='8' ry='8' width='80%' height='16' />
                            <rect x='0' y='48' rx='8' ry='8' width='60%' height='16' />
                        </ContentLoader>
                    </StackedBody>
                </>
            }
        >
            <MoqRuleEditorContent {...props} />
        </Suspense>
    );
};
